import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTranslation } from "react-i18next";
import {
  getCategoryFields,
  sendRequest,
  searchTicketsByNumber,
  isTicketNumberField,
} from "../request-orders.service";
import { toast } from "react-toastify";
import { formatDate } from "shared/helpers/utils";
import ViewTicket from "customer/orders/myBooking/component/viewTicket";
import CloseIcon from "shared/assets/images/svg/close_form.svg";
import "../requestOrders.scss";

/**
 * `<input type="date">` only accepts yyyy-MM-dd. The API sends an unzoned ISO
 * timestamp, so the date part is sliced off rather than parsed — going through
 * `new Date` would shift the day whenever the browser's offset is negative.
 */
const toDateInput = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

/**
 * Bitrix names these UF_RPA_{typeId}_ISSUSDATE, _CUSTOMERCOST and _PNR, so they are
 * matched on the field-name suffix — the caption is whatever the admin typed.
 *
 * The cost comes back as `netTotal`: the TicketDetails procedure selects the
 * Trip_Ticket.CustomerCost column but aliases it to NetTotal, so there is no
 * `customerCost` key on the response.
 */
const TICKET_AUTOFILL = {
  ISSUSDATE: (ticket) => toDateInput(ticket.issueDate),
  CUSTOMERCOST: (ticket) => (ticket.netTotal == null ? "" : String(ticket.netTotal)),
  PNR: (ticket) => ticket.pnr ?? "",
};

const NewRequestModal = ({ show, onHide, categoryId, onCreated }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [errors, setErrors] = useState({});
  // Ticket lookup: which field is searching, its candidate list, and the ticket on screen.
  const [searchingField, setSearchingField] = useState(null);
  const [ticketMatches, setTicketMatches] = useState({ fieldId: null, tickets: [] });
  const [viewedTicket, setViewedTicket] = useState(null);

  useEffect(() => {
    if (show && categoryId) {
      setLoadingFields(true);
      getCategoryFields(categoryId)
        .then((data) => {
          setFields(data);
          const initial = {};
          data.forEach((f) => {
            // The ticket lookup is always a single value, whatever Bitrix declared.
            const multiple = (f.isMultiple || f.IsMultiple) && !isTicketNumberField(f);
            initial[f.id] = multiple ? [] : "";
          });
          setFormData(initial);
          setFileData({});
          setErrors({});
          setTicketMatches({ fieldId: null, tickets: [] });
          setViewedTicket(null);
        })
        .catch(() => {})
        .finally(() => setLoadingFields(false));
    }
  }, [show, categoryId]);

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors((prev) => ({ ...prev, [fieldId]: null }));
    }
  };

  const selectTicket = (fieldId, ticket) => {
    const patch = { [fieldId]: String(ticket.ticketNumber ?? "") };

    fields.forEach((f) => {
      const suffix = Object.keys(TICKET_AUTOFILL).find((s) =>
        (f.id || "").toUpperCase().endsWith(`_${s}`)
      );
      if (suffix) patch[f.id] = TICKET_AUTOFILL[suffix](ticket);
    });

    setFormData((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
    setTicketMatches({ fieldId: null, tickets: [] });
    setViewedTicket(ticket);
  };

  const handleTicketSearch = async (field) => {
    const value = String(formData[field.id] ?? "").trim();
    if (!value) {
      toast.info(t("request-orders.enterTicketNumber"));
      return;
    }

    setSearchingField(field.id);
    setTicketMatches({ fieldId: null, tickets: [] });
    try {
      const tickets = await searchTicketsByNumber(value);
      if (tickets.length === 0) {
        toast.info(t("request-orders.ticketNotFound"));
      } else if (tickets.length === 1) {
        selectTicket(field.id, tickets[0]);
      } else {
        setTicketMatches({ fieldId: field.id, tickets });
      }
    } catch {
      // error handled by client
    } finally {
      setSearchingField(null);
    }
  };

  const handleFileChange = (fieldId, files, isMultiple) => {
    if (!files || files.length === 0) return;

    if (isMultiple) {
      const newFiles = Array.from(files);
      setFileData((prev) => ({
        ...prev,
        [fieldId]: [...(prev[fieldId] || []), ...newFiles],
      }));
      const readers = newFiles.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve([file.name, reader.result.split(",")[1]]);
            reader.readAsDataURL(file);
          })
      );
      Promise.all(readers).then((results) => {
        setFormData((prev) => ({
          ...prev,
          [fieldId]: [...(prev[fieldId] || []), ...results],
        }));
      });
    } else {
      const file = files[0];
      setFileData((prev) => ({ ...prev, [fieldId]: file }));
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        handleChange(fieldId, [file.name, base64]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (fieldId, index) => {
    if (index !== undefined) {
      setFileData((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
      }));
      setFormData((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
      }));
    } else {
      setFileData((prev) => ({ ...prev, [fieldId]: null }));
      handleChange(fieldId, "");
    }
  };

  const validate = () => {
    const newErrors = {};
    fields.forEach((f) => {
      const mandatory = f.isMandatory || f.IsMandatory;
      if (mandatory) {
        const val = formData[f.id];
        if (!val || (Array.isArray(val) && val.length === 0) || val === "") {
          newErrors[f.id] = t("request-orders.required");
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await sendRequest(categoryId, formData);
      toast.success(t("form.save-success"));
      onCreated();
    } catch {
      // error handled by client
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const type = (field.dataType || field.DataType || "").toLowerCase();

    // The Bitrix ticket-number field gets a lookup button whatever type it was declared as.
    if (isTicketNumberField(field)) {
      const matches = ticketMatches.fieldId === field.id ? ticketMatches.tickets : [];
      const busy = searchingField === field.id;

      return (
        <div className="ticket-field">
          <div className="ticket-input-row">
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              value={formData[field.id] || ""}
              onChange={(e) => {
                handleChange(field.id, e.target.value);
                setTicketMatches({ fieldId: null, tickets: [] });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleTicketSearch(field);
                }
              }}
            />
            <button
              type="button"
              className="btn-ticket-search"
              onClick={() => handleTicketSearch(field)}
              disabled={busy}
              title={t("request-orders.searchTicket")}
            >
              {busy ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                <SearchIcon />
              )}
            </button>
          </div>

          {matches.length > 0 && (
            <div className="ticket-matches">
              <div className="ticket-matches__title">
                {t("request-orders.ticketMatches", { count: matches.length })}
              </div>
              {matches.map((ticket, i) => (
                <button
                  key={`${ticket.ticketNumber}-${i}`}
                  type="button"
                  className="ticket-match"
                  onClick={() => selectTicket(field.id, ticket)}
                >
                  <span className="ticket-match__number">{ticket.ticketNumber}</span>
                  <span className="ticket-match__pax">{ticket.paxName}</span>
                  {/* One number can return a sale plus its refunds, so the status and
                      amount are what tell the rows apart. */}
                  <span className="ticket-match__meta">
                    {[
                      ticket.status,
                      ticket.pnr,
                      ticket.routes,
                      ticket.issueDate && formatDate(ticket.issueDate),
                      ticket.netTotal != null && Number(ticket.netTotal).toLocaleString(),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    switch (type) {
      case "enumeration": {
        const multiple = field.isMultiple || field.IsMultiple;

        if (multiple) {
          return (
            <MultiSelect
              options={field.lstofdata || []}
              value={Array.isArray(formData[field.id]) ? formData[field.id] : []}
              onChange={(vals) => handleChange(field.id, vals)}
            />
          );
        }

        return (
          <select
            className="form-select"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          >
            <option value="">{t("common.select-all")}</option>
            {(field.lstofdata || []).map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.name}
              </option>
            ))}
          </select>
        );
      }

      case "integer":
      case "double":
        return (
          <input
            type="number"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );

      case "file": {
        const multiple = field.isMultiple || field.IsMultiple;
        const currentFiles = fileData[field.id];

        if (multiple) {
          const fileList = Array.isArray(currentFiles) ? currentFiles : [];
          return (
            <div className="file-field">
              {fileList.length > 0 && (
                <div className="file-list">
                  {fileList.map((f, i) => (
                    <div key={i} className="file-selected">
                      <span className="file-name">{f.name}</span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => removeFile(field.id, i)}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <FileInput
                multiple
                onChange={(files) => handleFileChange(field.id, files, true)}
              />
            </div>
          );
        }

        return (
          <div className="file-field">
            {currentFiles ? (
              <div className="file-selected">
                <span className="file-name">{currentFiles.name}</span>
                <button
                  type="button"
                  className="remove-file-btn"
                  onClick={() => removeFile(field.id)}
                >
                  &times;
                </button>
              </div>
            ) : (
              <FileInput
                onChange={(files) => handleFileChange(field.id, files, false)}
              />
            )}
          </div>
        );
      }

      case "date":
      case "datetime":
        return (
          <input
            type="date"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );

      case "boolean":
        return (
          <div className="form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!formData[field.id]}
              onChange={(e) => handleChange(field.id, e.target.checked ? "1" : "0")}
            />
          </div>
        );

      case "string":
      default: {
        const rows = Number(field.rows || field.Rows || 1);

        if (rows > 1) {
          return (
            <textarea
              className="form-control memo-field"
              rows={rows}
              value={formData[field.id] || ""}
              onChange={(e) => handleChange(field.id, e.target.value)}
            />
          );
        }

        return (
          <input
            type="text"
            className="form-control"
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          />
        );
      }
    }
  };

  return (
    <React.Fragment>
      <SwipeableDrawer
        anchor="right"
        open={show}
        onClose={onHide}
        onOpen={() => {}}
      >
        <Box role="presentation" className="newRequestDrawer">
          <div className="row">
            <div className="col-12">
              <div className="drawerHeader">
                <div className="info">
                  <h2>{t("request-orders.newRequest")}</h2>
                </div>
                <div className="action">
                  <img src={CloseIcon} alt="close" onClick={onHide} />
                </div>
              </div>
            </div>
          </div>

          <div className="newRequestDrawer__body">
            {loadingFields ? (
              <div className="loading-spinner">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : (
              <div className="drawer-fields">
                {fields.map((field) => {
                  const mandatory = field.isMandatory || field.IsMandatory;
                  return (
                    <div key={field.id} className="form-group form-div mt-0 row g-3 align-items-center">
                      <label className="col-lg-4 col-form-label">
                        {field.name}
                        {mandatory && <small className="star-require"> *</small>}
                      </label>
                      <div className="col-lg-8">
                        {renderField(field)}
                        {errors[field.id] && (
                          <small className="text-danger">{errors[field.id]}</small>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="newRequestDrawer__footer">
            <button
              className="btn-blue"
              onClick={handleSubmit}
              disabled={loading || loadingFields}
            >
              {loading ? "..." : t("request-orders.submit")}
            </button>
            <button className="btn-border-blue" onClick={onHide}>
              {t("request-orders.cancel")}
            </button>
          </div>
        </Box>
      </SwipeableDrawer>

      <ViewTicket
        show={!!viewedTicket}
        onHide={() => setViewedTicket(null)}
        ticketData={viewedTicket}
      />
    </React.Fragment>
  );
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const MultiSelect = ({ options, value, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) {
      onChange(value.filter((v) => v !== sid));
    } else {
      onChange([...value, sid]);
    }
  };

  const selected = options.filter((o) => value.includes(String(o.id)));

  return (
    <div className="multi-select" ref={ref}>
      <div
        className="multi-select__control"
        onClick={() => setOpen((prev) => !prev)}
      >
        {selected.length > 0 ? (
          <div className="multi-select__tags">
            {selected.map((opt) => (
              <span key={opt.id} className="multi-select__tag">
                {opt.name}
                <button
                  type="button"
                  className="multi-select__tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(opt.id);
                  }}
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        ) : (
          <span className="multi-select__placeholder">
            {t("common.select-all")}
          </span>
        )}
        <i className="fas fa-chevron-down multi-select__arrow" />
      </div>

      {open && (
        <div className="multi-select__menu">
          {options.length === 0 ? (
            <div className="multi-select__empty">{t("common.no-data")}</div>
          ) : (
            options.map((opt) => (
              <label key={opt.id} className="multi-select__option">
                <input
                  type="checkbox"
                  checked={value.includes(String(opt.id))}
                  onChange={() => toggle(opt.id)}
                />
                <span>{opt.name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const FileInput = ({ onChange, multiple }) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  return (
    <div className="file-upload-area" onClick={() => ref.current?.click()}>
      <input
        type="file"
        ref={ref}
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => {
          onChange(e.target.files);
          e.target.value = "";
        }}
      />
      <span className="file-upload-text">
        <i className="fas fa-cloud-upload-alt" /> {t("request-orders.attachFile")}
      </span>
    </div>
  );
};

export default NewRequestModal;
